import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Star, BadgeCheck, Plus, ImagePlus } from "lucide-react";
import { adminApi, apiError } from "@/lib/adminApi";
import { imgUrl, Spinner, Card } from "@/components/admin/shared";

const blank = { name: "", location: "", rating: 5, title: "", text: "" };

export function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(blank);
  const refs = useRef({});

  const load = async () => {
    const r = await adminApi.get("/admin/reviews");
    setReviews(r.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, updates) => {
    setBusy(id);
    try {
      await adminApi.put(`/admin/reviews/${id}`, updates);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(null); }
  };

  const onPhoto = async (id, file) => {
    if (!file) return;
    setBusy(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await adminApi.post(`/admin/reviews/${id}/photo`, fd);
      toast.success("Photo replaced");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    setBusy(id);
    try {
      await adminApi.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      await load();
    } catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  const create = async () => {
    if (!form.name.trim() || !form.title.trim() || !form.text.trim()) { toast.error("Name, title and text are required"); return; }
    try {
      await adminApi.post("/admin/reviews", form);
      toast.success("Review added");
      setForm(blank); setCreating(false);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Create failed"); }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-reviews-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Reviews</h1>
          <p className="mt-2 text-sm text-[#8A7F86]">Replace photos, edit, verify or remove customer reviews. {reviews.length} total.</p>
        </div>
        <button onClick={() => setCreating((c) => !c)} data-testid="admin-add-review"
          className="flex items-center gap-2 rounded-full bg-[#232222] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] hover:bg-[#4A2A38]">
          <Plus className="h-3.5 w-3.5" /> Add review
        </button>
      </div>

      {creating && (
        <Card className="mt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="rounded-xl border border-[#E5DBC8] px-4 py-2.5 text-sm" data-testid="new-review-name" />
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="rounded-xl border border-[#E5DBC8] px-4 py-2.5 text-sm" />
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="rounded-xl border border-[#E5DBC8] px-4 py-2.5 text-sm sm:col-span-2" data-testid="new-review-title" />
            <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Review text" rows={3} className="resize-none rounded-xl border border-[#E5DBC8] px-4 py-2.5 text-sm sm:col-span-2" data-testid="new-review-text" />
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: +e.target.value })} className="rounded-xl border border-[#E5DBC8] px-4 py-2.5 text-sm">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
            </select>
          </div>
          <button onClick={create} data-testid="save-new-review" className="mt-4 rounded-full bg-[#FBC4A8] px-6 py-2.5 text-[0.62rem] font-semibold uppercase tracking-wider text-[#232222]">Save review</button>
        </Card>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {reviews.map((r) => (
          <Card key={r.id} className="flex gap-4" >
            <div className="flex-none">
              {r.photo ? (
                <img src={imgUrl(r.photo)} alt={r.name} className="h-24 w-24 rounded-xl object-cover" data-testid={`admin-review-photo-${r.id}`} />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-xl bg-[#F5EFE3] text-[#C7AFA5]"><ImagePlus className="h-6 w-6" /></div>
              )}
              <input ref={(el) => (refs.current[r.id] = el)} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                data-testid={`admin-review-photo-input-${r.id}`} onChange={(e) => onPhoto(r.id, e.target.files?.[0])} />
              <button onClick={() => refs.current[r.id]?.click()} disabled={busy === r.id} data-testid={`admin-review-replace-${r.id}`}
                className="mt-2 flex w-24 items-center justify-center gap-1 rounded-full bg-[#232222] py-1.5 text-[0.55rem] uppercase tracking-wider text-[#F5EFE3] hover:bg-[#4A2A38] disabled:opacity-60">
                {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Replace
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                {[...Array(r.rating)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#F4B999] text-[#F4B999]" />)}
              </div>
              <p className="mt-1 truncate font-display text-base text-[#2C2B2C]">"{r.title}"</p>
              <p className="mt-1 line-clamp-2 text-xs text-[#8A7F86]">{r.text}</p>
              <p className="mt-1.5 text-xs font-medium text-[#2C2B2C]">{r.name} <span className="font-light text-[#8A7F86]">· {r.location}</span></p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={() => patch(r.id, { verified: !r.verified })} data-testid={`admin-review-verify-${r.id}`}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[0.55rem] font-semibold uppercase tracking-wider ${r.verified ? "bg-[#E0EAF5] text-[#3B82F6]" : "bg-[#F0E7E0] text-[#9A8E88]"}`}>
                  <BadgeCheck className="h-3 w-3" /> {r.verified ? "Verified" : "Unverified"}
                </button>
                <button onClick={() => patch(r.id, { approved: !r.approved })}
                  className={`rounded-full px-3 py-1 text-[0.55rem] font-semibold uppercase tracking-wider ${r.approved ? "bg-[#E3F0E3] text-[#4A5D4E]" : "bg-[#F0E7E0] text-[#9A8E88]"}`}>
                  {r.approved ? "Live" : "Hidden"}
                </button>
                <button onClick={() => remove(r.id)} data-testid={`admin-review-delete-${r.id}`} className="ml-auto text-[#C77] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
